import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './use-toast';
import client from '../lib/api';

/** Telegram üyelik kapısının durumunu ve bağlama akışını yürütür.
 *
 * Kapı iki ekranda birden gerekir (promosyon kodu ve deneme bonusu kartı),
 * bu yüzden mantık sayfada değil burada durur — ayrışsalardı biri "üye
 * değilsiniz" derken diğeri kod alanını açardı.
 *
 * ``required`` kapının o ekranda anlamlı olup olmadığını söyler; kapalıyken
 * hiç istek atılmaz. ``bonusId`` verilirse üyelik o kampanyanın kendi
 * kanalında sorulur (kampanyada yazılan Telegram adresi); verilmezse genel
 * topluluk grubunda. Asıl denetim her hâlde sunucudadır (promo_router
 * ``_require_telegram_membership``): arayüz yalnızca oyuncunun 403'e kadar
 * gelmesini önler.
 */
export const usePromoTelegram = (required, bonusId = '') => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const userId = user?.id;

  const refresh = useCallback(async () => {
    if (!required || !userId) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await client.get(
        '/telegram/link', bonusId ? { params: { bonus_id: bonusId } } : undefined,
      );
      setStatus(data);
      if (data.linked) setLinkUrl('');
    } catch (error) {
      setStatus({
        configured: false,
        linked: false,
        member: false,
        error: error.response?.data?.detail || 'Telegram durumu alınamadı',
      });
    } finally {
      setLoading(false);
    }
  }, [required, userId, bonusId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Oyuncu Telegram sekmesinden geri döndüğünde üyelik durumu kendiliğinden
  // yenilenir; ayrıca kapı kartındaki düğmeyle elle de sorgulanabilir.
  useEffect(() => {
    if (!required || !userId || status?.member) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [required, status?.member, refresh, userId]);

  const link = useCallback(async () => {
    setLinking(true);
    let popup = null;
    try {
      popup = window.open('', '_blank');
      if (popup) popup.opener = null;
    } catch {
      popup = null;
    }
    try {
      const { data } = await client.post('/telegram/link');
      setLinkUrl(data.url);
      let opened = false;
      if (popup && !popup.closed) {
        try {
          popup.location.replace(data.url);
          opened = true;
        } catch {
          opened = false;
        }
      }
      if (!opened) window.location.href = data.url;
    } catch (error) {
      if (popup && !popup.closed) popup.close();
      toast({
        title: 'Telegram bağlantısı oluşturulamadı',
        description: error.response?.data?.detail || 'Lütfen tekrar deneyin',
      });
    } finally {
      setLinking(false);
    }
  }, [toast]);

  return {
    status, loading, linking, linkUrl, refresh, link,
  };
};

export default usePromoTelegram;
